import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, CheckCircle, AlertCircle, X } from "lucide-react";
import { fetchVoiceTemplateAgents, duplicateVoiceTemplateToAgent, clearVoiceTemplateCache } from "@/services/voiceTemplateService";
import { VoiceAgent } from "@/services/voiceAgentService";
import { checkPlanLimits } from "@/lib/planLimits";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { DialogPortal, DialogOverlay } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

// Define the voice template type based on what the service returns
type VoiceTemplate = {
  id: string;
  title: string;
  agent_name: string;
  description?: string | null;
  first_message: string;
  personas?: any;
  industries?: any;
  [key: string]: any;
};

interface VoiceAgentCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (agent: VoiceAgent) => void;
}

export default function VoiceAgentCreationModal({ 
  isOpen, 
  onClose, 
  onSuccess 
}: VoiceAgentCreationModalProps) {
  const { user, organization } = useAuth();
  const { toast } = useToast();
  
  const [templates, setTemplates] = useState<VoiceTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<VoiceTemplate | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [planLimitError, setPlanLimitError] = useState<string | null>(null);

  // Load voice templates when modal opens
  useEffect(() => {
    if (isOpen && organization?.id) {
      loadTemplates();
    }
  }, [isOpen, organization?.id]);

  const loadTemplates = async () => {
    setIsLoading(true);
    try {
      // Clear cache to ensure fresh data after RLS policy update
      clearVoiceTemplateCache();
      
      const result = await fetchVoiceTemplateAgents({ is_template: true });
      if ('error' in result && result.error) {
        throw result.error;
      }
      setTemplates(result.data || []);
      console.log('Loaded voice templates:', result.data?.length || 0);
    } catch (error) {
      console.error('Error loading voice templates:', error);
      toast({
        variant: "destructive",
        title: "Failed to load templates",
        description: "Could not load voice agent templates. Please try again."
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTemplateSelect = async (template: VoiceTemplate) => {
    if (!user?.id || !organization?.id) {
      toast({
        variant: "destructive",
        title: "Authentication required",
        description: "Please log in to create a voice agent."
      });
      return;
    }

    // Check plan limits first
    try {
      const limitError = await checkPlanLimits(organization.id);
      if (limitError) {
        setPlanLimitError(limitError.message);
        return;
      }
    } catch (error) {
      console.error('Error checking plan limits:', error);
    }

    setSelectedTemplate(template);
    setIsCreating(true);
    setPlanLimitError(null);

    try {
      const { success, agentId, error, data } = await duplicateVoiceTemplateToAgent(
        template.id,
        user.id,
        organization.id
      );

      if (!success || !data) {
        throw new Error(error || "Failed to create voice agent from template");
      }

      toast({
        title: "Voice agent created",
        description: `${template.title} has been created successfully.`
      });

      setIsSuccess(true);
      
      // Show success animation for 2 seconds, then close and redirect
      setTimeout(() => {
        onSuccess(data);
        handleClose();
      }, 2000);

    } catch (error) {
      console.error('Failed to create voice agent:', error);
      toast({
        variant: "destructive",
        title: "Failed to create voice agent",
        description: error instanceof Error ? error.message : "An unexpected error occurred."
      });
      setIsCreating(false);
      setSelectedTemplate(null);
    }
  };

  const handleClose = () => {
    if (!isCreating) {
      setTemplates([]);
      setSelectedTemplate(null);
      setIsSuccess(false);
      setPlanLimitError(null);
      onClose();
    }
  };



  // Success state UI (matching chat wizard)
  if (isSuccess) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()} modal={true}>
        <DialogPortal>
          <DialogOverlay className="bg-black/40" />
          <DialogPrimitive.Content
            className={cn(
              "fixed left-[50%] top-[50%] z-50 w-[calc(100vw-48px)] h-[calc(100vh-64px)] sm:w-[calc(100vw-80px)] md:w-[90vw] max-w-7xl translate-x-[-50%] translate-y-[-50%] rounded-xl border border-gray-200 bg-white p-0 shadow-xl overflow-hidden duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]"
            )}
          >
            <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 py-8">
              <div className="mb-8">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto animate-bounce" />
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-4 animate-fade-in">
                🎙️ Building your voice agent...
              </h2>
              <p className="text-gray-600 text-lg animate-fade-in">
                Your AI voice agent will be ready shortly
              </p>
            </div>
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>
    );
  }

  // Main modal UI (matching chat wizard step 2)
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()} modal={true}>
      <DialogPortal>
        <DialogOverlay className="bg-black/40" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 w-[calc(100vw-48px)] h-[calc(100vh-64px)] sm:w-[calc(100vw-80px)] md:w-[90vw] max-w-7xl translate-x-[-50%] translate-y-[-50%] rounded-xl border border-gray-200 bg-white p-0 shadow-xl overflow-hidden duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]"
          )}
        >
          <Button 
            variant="ghost" 
            size="icon" 
            className="absolute right-4 top-4 z-10"
            onClick={handleClose}
            disabled={isCreating}
          >
            <X className="h-6 w-6" />
          </Button>

          <div className="mx-auto py-6 px-4 md:px-8 w-full h-full overflow-y-auto">
            <div className="py-6">
              <h1 className="text-xl text-center text-gray-600 mb-4">Create New Voice Agent</h1>

              {/* Plan limit error alert */}
              {planLimitError && (
                <Alert variant="destructive" className="mb-6">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Plan Limit Reached</AlertTitle>
                  <AlertDescription>
                    {planLimitError}
                  </AlertDescription>
                </Alert>
              )}

              <div className="px-4 py-6">
                <div className="mb-12 text-center">
                  <h2 className="text-3xl font-bold mb-3">Voice Agent Templates</h2>
                  <p className="text-gray-600 mb-4">
                    Choose a template to create your voice agent.
                  </p>
                </div>

                {isLoading ? (
                  <div className="flex justify-center py-12">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Template grid */}
                    {templates.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {templates.map((template) => (
                          <div
                            key={template.id}
                            className={`p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${
                              selectedTemplate?.id === template.id
                                ? "border-[#ecb22d] bg-[#ecb22d]/10"
                                : "border-gray-200 hover:border-gray-300"
                            } ${isCreating ? "opacity-50 pointer-events-none" : ""}`}
                            onClick={() => !isCreating && handleTemplateSelect(template)}
                          >
                            <div className="space-y-3">
                              {/* Template header */}
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h3 className="text-base font-semibold mb-1">{template.title}</h3>
                                  <p className="text-sm text-gray-600 mb-2">{template.agent_name}</p>
                                </div>
                                {selectedTemplate?.id === template.id && isCreating && (
                                  <Loader2 className="h-5 w-5 animate-spin text-[#ecb22d]" />
                                )}
                              </div>

                              {/* Description */}
                              {template.description && (
                                <p className="text-gray-600 text-sm line-clamp-3">
                                  {template.description}
                                </p>
                              )}

                              {/* Template metadata */}
                              <div className="flex items-center gap-4 text-xs text-gray-500">
                                {template.personas && (
                                  <span className="flex items-center gap-1">
                                    👤 {template.personas.avatar_name}
                                  </span>
                                )}
                                {template.industries && (
                                  <span className="flex items-center gap-1">
                                    {template.industries.emoji} {template.industries.name}
                                  </span>
                                )}
                              </div>


                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-gray-600 mb-4">
                          No voice agent templates available.
                        </p>
                        <p className="text-gray-500 text-sm">
                          Contact your administrator to add voice agent templates.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
} 